import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RuntsCerca } from "@/components/RuntsCerca";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Briefcase, Plus, Search, Users, MonitorSmartphone, Building } from "lucide-react";

export const CANALE_STYLE = {
  presenza: { cls: "bg-slate-100 text-slate-700 border-slate-200", Icon: Building },
  mista: { cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: Users },
  distanza: { cls: "bg-teal-50 text-teal-800 border-teal-200", Icon: MonitorSmartphone },
};
const CANALI_INFO = {
  presenza: "Cliente e intermediario sono presenti insieme in agenzia: percorso compliance ridotto.",
  mista: "Una parte avviene a distanza (es. intervista in agenzia e firma da casa): si aggiungono supporto durevole e conferma di ricezione.",
  distanza: "Nessun incontro di persona: protocollo completo con registrazione delle conversazioni, informativa sul recesso e passaggi obbligati.",
};

export function CanaleBadge({ canale, label }) {
  const s = CANALE_STYLE[canale] || CANALE_STYLE.presenza;
  return <span className={`inline-flex items-center gap-1 text-xs border rounded-full px-2 py-0.5 ${s.cls}`} data-testid={`canale-badge-${canale}`}><s.Icon className="h-3 w-3" />{label}</span>;
}

function NuovaTrattativa({ open, onOpenChange }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [ris, setRis] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [f, setF] = useState({ cliente: { anagrafica_id: "", nome: "", email: "", cellulare: "" }, canale: "", prodotto_code: "", origine_contatto: "", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/catalogo/prodotti").then(({ data }) => setProdotti(data)).catch(() => {}); }, [open]);
  const cerca = async () => { const { data } = await api.get("/anagrafiche/", { params: { q, limit: 15 } }); setRis(data); };
  const scegli = (a) => { setF({ ...f, cliente: { anagrafica_id: a.id, nome: a.ragione_sociale, email: a.email || "", cellulare: a.cellulare || a.telefono || "" } }); setRis([]); setQ(""); };
  const crea = async () => {
    setBusy(true);
    try {
      const prod = prodotti.find((p) => p.code === f.prodotto_code);
      const { data } = await api.post("/trattative", { ...f, prodotto_nome: prod?.nome || "" });
      toast.success(`Trattativa ${data.codice} aperta`);
      nav(`/trattative/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="trt-new-dialog">
        <DialogHeader><DialogTitle>Nuova trattativa</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>Cliente</Label>
            {f.cliente.anagrafica_id ? (
              <div className="flex items-center justify-between rounded-lg border border-teal-200 bg-teal-50/50 px-3 py-2 text-sm" data-testid="trt-new-cliente-scelto">
                <span><b>{f.cliente.nome}</b> · da anagrafica</span>
                <Button variant="ghost" size="sm" onClick={() => setF({ ...f, cliente: { anagrafica_id: "", nome: "", email: "", cellulare: "" } })}>Cambia</Button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <Input placeholder="Cerca in anagrafica (nome, CF, P.IVA, email)" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && cerca()} data-testid="trt-new-cerca" />
                  <Button variant="outline" onClick={cerca} data-testid="trt-new-cerca-btn"><Search className="h-4 w-4" /></Button>
                </div>
                {ris.length > 0 && (
                  <div className="border rounded-lg divide-y max-h-44 overflow-y-auto" data-testid="trt-new-risultati">
                    {ris.map((a) => <button key={a.id} onClick={() => scegli(a)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" data-testid={`trt-new-ana-${a.id}`}>{a.ragione_sociale} <span className="text-slate-400">{a.partita_iva || a.codice_fiscale || ""} {a.comune || ""}</span></button>)}
                  </div>
                )}
                <Input placeholder="…oppure scrivi il nome di un nuovo cliente" value={f.cliente.nome} onChange={(e) => setF({ ...f, cliente: { ...f.cliente, nome: e.target.value, codice_fiscale: "" } })} data-testid="trt-new-nome" />
                <RuntsCerca onScegli={(e) => setF({ ...f, cliente: { ...f.cliente, anagrafica_id: "", nome: e.denominazione, codice_fiscale: e.codice_fiscale } })} label="…oppure cerca un ETS nel RUNTS" />
                {f.cliente.codice_fiscale && <p className="text-[11px] text-slate-500" data-testid="trt-new-cf">Codice fiscale {f.cliente.codice_fiscale}</p>}
              </>
            )}
          </div>
          <div className="space-y-2">
            <Label>Canale di vendita</Label>
            <div className="grid sm:grid-cols-3 gap-2">
              {[["presenza", "In presenza"], ["mista", "Mista"], ["distanza", "Interamente a distanza"]].map(([k, l]) => {
                const S = CANALE_STYLE[k];
                return (
                  <button key={k} onClick={() => setF({ ...f, canale: k })} data-testid={`trt-new-canale-${k}`}
                    className={`text-left rounded-lg border p-3 transition-colors ${f.canale === k ? "border-teal-600 ring-2 ring-teal-600/20 bg-teal-50/40" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="flex items-center gap-1.5 font-medium text-sm text-slate-900"><S.Icon className="h-4 w-4" />{l}</div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">{CANALI_INFO[k]}</p>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Prodotto (facoltativo)</Label>
              <select value={f.prodotto_code} onChange={(e) => setF({ ...f, prodotto_code: e.target.value })} className="w-full h-9 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="trt-new-prodotto">
                <option value="">— da definire —</option>
                {prodotti.map((p) => {
                  const no = f.canale === "distanza" && (/\bvita\b/i.test(p.famiglia || "") || p.non_vendibile_distanza);
                  return <option key={p.code} value={p.code} disabled={no}>{p.nome}{no ? " — non vendibile a distanza" : ""}</option>;
                })}
              </select>
            </div>
            <div className="space-y-1.5"><Label>Come è arrivato il contatto</Label>
              <select value={f.origine_contatto} onChange={(e) => setF({ ...f, origine_contatto: e.target.value })} className="w-full h-9 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="trt-new-origine">
                <option value="">— scegli —</option>
                {["Telefono", "Email", "Sito / pagina dedicata", "Social", "Videochiamata", "In agenzia", "Segnalazione (es. Banca Etica)", "Cliente già in portafoglio"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={crea} disabled={busy || !f.canale || !f.cliente.nome.trim()} className="brand-gradient text-white border-0" data-testid="trt-new-crea">Apri trattativa</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Trattative() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [filtro, setFiltro] = useState({ canale: "", stato: "aperta", q: "" });
  const [open, setOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  useEffect(() => { if (params.get("nuova") === "1") { setOpen(true); setParams({}, { replace: true }); } }, [params, setParams]);
  const load = useCallback(() => { api.get("/trattative", { params: filtro }).then(({ data }) => setItems(data)).catch(() => {}); }, [filtro]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <button onClick={() => nav("/")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-4" data-testid="trt-back"><ArrowLeft className="h-4 w-4" /> Torna alla Suite</button>
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="flex items-center gap-2 text-teal-700 text-xs font-semibold tracking-wide"><Briefcase className="h-4 w-4" /> TRATTATIVE</div>
            <h1 className="text-3xl font-bold text-slate-900 mt-1">Trattative di vendita</h1>
            <p className="text-sm text-slate-500 mt-1">Ogni vendita segue il protocollo del suo canale: in presenza, mista o interamente a distanza.</p>
          </div>
          <div className="flex flex-wrap gap-2"><Button variant="outline" onClick={() => nav("/trattative/inviti-adesione")} data-testid="trt-inviti-adesione">Inviti adesione online</Button><Button variant="outline" onClick={() => nav("/trattative/adesioni")} data-testid="trt-adesioni-online">Adesioni online</Button><Button variant="outline" onClick={() => nav("/trattative/stato")} data-testid="trt-stato-pratiche">Stato pratiche</Button><Button variant="outline" onClick={() => nav("/trattative/recessi")} data-testid="trt-scadenzario-recessi">Scadenzari</Button>
          <Button onClick={() => setOpen(true)} className="brand-gradient text-white border-0" data-testid="trt-nuova"><Plus className="h-4 w-4 mr-2" /> Nuova trattativa</Button></div>
        </div>
        <div className="flex gap-2 flex-wrap mb-4">
          <Input placeholder="Cerca cliente o codice" value={filtro.q} onChange={(e) => setFiltro({ ...filtro, q: e.target.value })} className="max-w-xs bg-white" data-testid="trt-filtro-q" />
          <select value={filtro.canale} onChange={(e) => setFiltro({ ...filtro, canale: e.target.value })} className="h-9 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="trt-filtro-canale">
            <option value="">Tutti i canali</option><option value="presenza">In presenza</option><option value="mista">Mista</option><option value="distanza">Interamente a distanza</option>
          </select>
          <select value={filtro.stato} onChange={(e) => setFiltro({ ...filtro, stato: e.target.value })} className="h-9 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="trt-filtro-stato">
            <option value="">Tutti gli stati</option><option value="aperta">Aperte</option><option value="conclusa">Concluse</option><option value="annullata">Annullate</option>
          </select>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 divide-y" data-testid="trt-lista">
          {items.length === 0 && <p className="p-8 text-center text-sm text-slate-400" data-testid="trt-lista-vuota">Nessuna trattativa. Aprine una con "Nuova trattativa".</p>}
          {items.map((t) => (
            <button key={t.id} onClick={() => nav(`/trattative/${t.id}`)} className="w-full text-left px-5 py-4 hover:bg-slate-50 flex items-center justify-between gap-3" data-testid={`trt-row-${t.id}`}>
              <div>
                <div className="font-medium text-slate-900">{t.cliente?.nome}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.codice} · {t.prodotto_nome || "prodotto da definire"} · aggiornata {new Date(t.updated_at).toLocaleDateString("it-IT")}</div>
              </div>
              <div className="flex items-center gap-2"><CanaleBadge canale={t.canale} label={t.canale_label} /><span className="text-xs text-slate-500">{t.stato_label}</span></div>
            </button>
          ))}
        </div>
      </div>
      <NuovaTrattativa open={open} onOpenChange={setOpen} />
    </div>
  );
}
